import { useEffect, useRef, useState } from "react";

export type ScrollOverflow = "none" | "start" | "end" | "both";

// Sub-pixel widths leave scrollLeft a fraction short of the true end.
const EDGE_SLACK_PX = 1;

function measure(el: HTMLElement): ScrollOverflow {
  const hiddenBefore = el.scrollLeft > EDGE_SLACK_PX;
  const hiddenAfter = el.scrollLeft + el.clientWidth < el.scrollWidth - EDGE_SLACK_PX;
  if (hiddenBefore && hiddenAfter) return "both";
  if (hiddenBefore) return "start";
  if (hiddenAfter) return "end";
  return "none";
}

export function useScrollOverflow<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [overflow, setOverflow] = useState<ScrollOverflow>("none");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setOverflow(measure(el));
    update();
    el.addEventListener("scroll", update, { passive: true });
    const resizes = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    resizes?.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      resizes?.disconnect();
    };
  }, []);

  return { ref, overflow };
}
